import React, { useEffect, useState } from 'react';
import { parseDesc } from '../hooks/useEventos';
import { normISO, val, fmtDuracion } from '../utils/surgeryHelpers';

/**
 * Banner de cirugías actualmente en curso, determinado por la hora de
 * inicio y fin PROGRAMADAS del agendamiento (no requiere que se hayan
 * registrado tiempos quirúrgicos manuales). Se considera "en curso"
 * cuando la hora actual cae dentro de ese rango y el agendamiento no
 * está cancelado. Al hacer clic en una tarjeta se abre el modal de
 * Tiempos quirúrgicos para registrar el avance real.
 */
const EnCirugiaBanner = ({ docs, onAbrirTiempos }) => {
  const [ahora, setAhora] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setAhora(Date.now()), 30000); // se actualiza cada 30s
    return () => clearInterval(t);
  }, []);

  const enCirugia = (docs || []).filter((doc) => {
    const ev = doc?.event || {};
    const estado = (ev.summary || '').toLowerCase();
    if (estado === 'cancelada' || estado === 'realizada') return false;
    const startMs = ev.start?.dateTime ? new Date(normISO(ev.start.dateTime)).getTime() : null;
    const endMs   = ev.end?.dateTime   ? new Date(normISO(ev.end.dateTime)).getTime()   : null;
    if (!startMs || !endMs) return false;
    return ahora >= startMs && ahora <= endMs;
  });

  if (enCirugia.length === 0) return null;

  return (
    <div style={{
      alignItems: 'center', background: '#fdf1f2', border: '1px solid #f6b8be', borderRadius: '10px',
      display: 'flex', gap: '10px', marginBottom: '14px', overflow: 'hidden', padding: '7px 10px',
    }}>
      {/* Etiqueta */}
      <div style={{ alignItems: 'center', display: 'flex', flexShrink: 0, gap: '6px' }}>
        <span style={{ position: 'relative', display: 'inline-flex', height: '8px', width: '8px' }}>
          <span style={{ position: 'absolute', height: '100%', width: '100%', borderRadius: '50%', background: '#e12f3b', opacity: .6, animation: 'pulseDot 1.6s ease-in-out infinite' }} />
          <span style={{ position: 'relative', height: '8px', width: '8px', borderRadius: '50%', background: '#e12f3b' }} />
        </span>
        <span style={{ color: '#991b1b', fontSize: '11px', fontWeight: 800, whiteSpace: 'nowrap' }}>
          {enCirugia.length === 1 ? '1 en curso' : `${enCirugia.length} en curso`}
        </span>
      </div>

      <div style={{ background: '#f6b8be', flexShrink: 0, height: '18px', width: '1px' }} />

      {/* Chips horizontales */}
      <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '2px' }}>
        {enCirugia.map((doc) => (
          <ChipEnCirugia key={doc.id} doc={doc} ahora={ahora} onClick={() => onAbrirTiempos && onAbrirTiempos(doc)} />
        ))}
      </div>

      <style>{`
        @keyframes pulseDot {
          0%   { transform: scale(1);   opacity: .6; }
          70%  { transform: scale(2.2); opacity: 0;  }
          100% { transform: scale(2.2); opacity: 0;  }
        }
      `}</style>
    </div>
  );
};

const ChipEnCirugia = ({ doc, ahora, onClick }) => {
  const ev = doc?.event || {};
  const fields = parseDesc(ev.description);

  const quirofano = val(fields['AppointmentType']) || 'Quirófano';
  const medico     = val(fields['Medico']);

  const startMs = new Date(normISO(ev.start?.dateTime)).getTime();
  const endMs   = new Date(normISO(ev.end?.dateTime)).getTime();

  const transcurridoMin = Math.max(0, Math.round((ahora - startMs) / 60000));
  const restanteMin     = Math.max(0, Math.round((endMs - ahora) / 60000));
  const porFinalizar     = restanteMin <= 15;

  return (
    <button
      onClick={onClick}
      title={medico ? `${medico} · termina en ${fmtDuracion(restanteMin)}` : `Termina en ${fmtDuracion(restanteMin)}`}
      style={{
        alignItems: 'center', background: '#fff', border: `1px solid ${porFinalizar ? '#fcd34d' : '#e5e7eb'}`,
        borderRadius: '20px', cursor: 'pointer', display: 'flex', flexShrink: 0, gap: '6px',
        padding: '4px 10px', whiteSpace: 'nowrap',
      }}
    >
      <span style={{ color: '#039490', fontSize: '10px', fontWeight: 800, textTransform: 'uppercase' }}>{quirofano}</span>
      {medico && (
        <span style={{ color: '#374151', fontSize: '11px', fontWeight: 600, maxWidth: '110px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {medico}
        </span>
      )}
      <span style={{
        color: porFinalizar ? '#d97706' : '#7c3aed', fontSize: '10px', fontWeight: 800,
      }}>
        ⏱ {fmtDuracion(transcurridoMin)}
      </span>
      {porFinalizar && <span style={{ color: '#d97706', fontSize: '11px' }}>⚠</span>}
    </button>
  );
};

export default EnCirugiaBanner;
